import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import {
    isIPhone,
    isIPad,
    isAndroid,
    isAndroidTablet,
    isDesktop,
} from '../../util/deviceDetection';
import { isSafari } from '../../util/browserDetection';
import feather from 'feather-icons';

function PWAInstallSettingsSection() {
    const { t } = useLanguage();
    const [deviceType, setDeviceType] = useState(null);

    useEffect(() => {
        // Desktop Safari doesn't support the install prompt the way Chrome/Edge does.
        if (isSafari() && isDesktop) {
            setDeviceType('desktop-safari');
            return;
        }

        if (isIPhone || isIPad) {
            setDeviceType('ios');
        } else if (isAndroid || isAndroidTablet) {
            setDeviceType('android');
        } else if (isDesktop) {
            setDeviceType('desktop');
        }
    }, []);

    useEffect(() => {
        if (deviceType) feather.replace();
    }, [deviceType]);

    const handleAndroidInstall = async () => {
        try {
            let dp = window.deferredPromptGlobal;

            // If not available yet, wait briefly — on Android the event may fire slightly late
            if (!dp) {
                await new Promise((resolve) => setTimeout(resolve, 500));
                dp = window.deferredPromptGlobal;
            }

            if (dp) {
                dp.prompt();
                const { outcome } = await dp.userChoice;
                window.deferredPromptGlobal = null;

                if (outcome === 'accepted') {
                    localStorage.setItem('xpenso-install-done', 'true');
                }
                return;
            }

            // Fallback: guide user to use browser menu
            window.alert(t('installAppNotAvailableAndroid'));
        } catch {
            // no-op
        }
    };

    const handleIOSInstall = async () => {
        try {
            const el = document.getElementById('ios-install-sheet');
            if (!el) return;

            document.querySelectorAll('.offcanvas-backdrop').forEach((n) => n.remove());

            const OffcanvasCtor = window.bootstrap?.Offcanvas;
            if (!OffcanvasCtor) {
                return;
            }

            const instance = OffcanvasCtor.getOrCreateInstance
                ? OffcanvasCtor.getOrCreateInstance(el)
                : new OffcanvasCtor(el);

            instance.show();

            el.addEventListener(
                'hidden.bs.offcanvas',
                () => {
                    document.querySelectorAll('.offcanvas-backdrop').forEach((n) => n.remove());
                    document.body.style.overflow = 'auto';
                    document.body.style.position = 'relative';
                },
                { once: true }
            );
        } catch {
            // no-op (avoid console logs in production)
        }
    };

    const handleDesktopInstall = async () => {
        try {
            const dp = window.deferredPromptGlobal;
            if (!dp) {
                // Desktop fallback: show user where the browser install UI is.
                window.alert(t('installAppNotAvailableDesktop'));
                return;
            }

            dp.prompt();
            await dp.userChoice;
            window.deferredPromptGlobal = null;
        } catch {
            // no-op (avoid console logs in production)
        }
    };

    if (!deviceType) return null;

    const contentByDevice = () => {
        if (deviceType === 'ios' || deviceType === 'desktop-safari') {
            return (
                <div className="setting-item">
                    <div className="setting-info">
                        <h4>{t('installApp')}</h4>
                        <p className="setting-description">
                            {deviceType === 'desktop-safari'
                                ? t('installAppDescSafariDesktop')
                                : t('installAppDescIOS')}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleIOSInstall}
                        className="settings-pill-btn"
                        aria-label={t('viewInstructions')}
                    >
                        <span className="settings-pill-btn__text">{t('viewInstructions')}</span>
                        <i className="icon" data-feather="arrow-right"></i>
                    </button>
                </div>
            );
        }

        if (deviceType === 'android') {
            return (
                <div className="setting-item">
                    <div className="setting-info">
                        <h4>{t('installApp')}</h4>
                        <p className="setting-description">{t('installAppDescAndroid')}</p>
                    </div>

                    <button
                        type="button"
                        onClick={handleAndroidInstall}
                        className="settings-pill-btn"
                        aria-label={t('install')}
                    >
                        <span className="settings-pill-btn__text">{t('install')}</span>
                        <i className="icon" data-feather="download"></i>
                    </button>
                </div>
            );
        }

        // desktop
        return (
            <div className="setting-item">
                <div className="setting-info">
                    <h4>{t('installApp')}</h4>
                    <p className="setting-description">{t('installAppDescDesktop')}</p>
                </div>

                <button
                    type="button"
                    onClick={handleDesktopInstall}
                    className="settings-pill-btn"
                    aria-label={t('install')}
                >
                    <span className="settings-pill-btn__text">{t('install')}</span>
                    <i className="icon" data-feather="download"></i>
                </button>
            </div>
        );
    };

    return (
        <div className="settings-section">
            <div className="settings-header">
                <div className="settings-icon install">
                    <i className="icon" data-feather="download"></i>
                </div>
                <h3 className="settings-title">{t('installApp')}</h3>
            </div>

            <div className="settings-content">{contentByDevice()}</div>
        </div>
    );
}

export default PWAInstallSettingsSection;
